<template>
  <v-dialog :value="value" max-width="900" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <ValidationObserver ref="formObserver">
          <v-form :disabled="isLoading">
            <v-container>
              <v-row>
                <v-col cols="12" sm="8">
                  <vee-text-field v-model="contentPack.name" rules="required|max:128" label="Name*" />
                </v-col>

                <v-col cols="12" sm="4">
                  <v-checkbox v-model="contentPack.active" label="Active" />
                </v-col>

                <v-col cols="12">
                  <vee-text-field
                    v-model="contentPack.displayName"
                    rules="required|max:256"
                    label="Display Name*"
                  />
                </v-col>

                <v-col cols="12">
                  <vee-text-field
                    v-model="contentPack.sku"
                    rules="max:128"
                    label="SKU"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="contentPack.description"
                    label="Description"
                    outlined
                    auto-grow
                    rows="4"
                  />
                </v-col>

                <v-col cols="12">
                  <content-pack-sections
                    :item="contentPack"
                    :is-loading="isLoading"
                    @update:item="onUpdateContentPack"
                  />
                </v-col>

                <v-col v-if="contentPack.id" cols="12">
                  <v-card outlined class="academy-assignment-panel">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      Academies
                    </v-card-title>
                    <v-card-text class="py-2">
                      <v-list v-if="assignedAcademies.length" dense class="py-0">
                        <v-list-item class="academy-assignment-header px-0">
                          <v-list-item-content>
                            <v-list-item-title class="text-caption font-weight-bold">
                              Academy
                            </v-list-item-title>
                          </v-list-item-content>
                          <div class="academy-assignment-purchased text-caption font-weight-bold">
                            Purchased
                          </div>
                          <v-list-item-action />
                        </v-list-item>
                        <v-list-item v-for="academy in assignedAcademies" :key="academy.id">
                          <v-list-item-content>
                            <v-list-item-title>{{ academy.name }}</v-list-item-title>
                          </v-list-item-content>
                          <div class="academy-assignment-purchased text-body-2">
                            {{ formatPurchased(academy.purchased) }}
                          </div>
                          <v-list-item-action>
                            <v-btn icon small @click="removeAcademy(academy.id)">
                              <v-icon small>
                                {{ icons.mdiDeleteOutline }}
                              </v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                      <div v-else class="text-body-2 grey--text">
                        No academies assigned.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn text :loading="isLoading" @click="onSave">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  computed, onMounted, ref, nextTick, watch,
} from 'vue'
import { mdiDeleteOutline } from '@mdi/js'
import { ValidationObserver } from 'vee-validate'
import ContentPackSections from '@/views/contentPacks/partials/ContentPackSections.vue'
import useContentPackAcademies from '@/composables/api/contentPackAcademies'

export default {
  components: {
    ValidationObserver,
    ContentPackSections,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { fetchAcademiesByContentPackId, deleteContentPackAcademy, formatPurchased } = useContentPackAcademies()
    const contentPack = ref({
      active: true,
      deleted: false,
      ...props.item,
    })
    const assignedAcademies = ref([])
    const formObserver = ref(null)

    const formTitle = computed(() => (contentPack.value.id ? 'Edit Content Pack' : 'Create Content Pack'))

    const loadAcademies = async () => {
      if (!contentPack.value.id) {
        assignedAcademies.value = []

        return
      }

      assignedAcademies.value = await fetchAcademiesByContentPackId(contentPack.value.id)
    }

    onMounted(() => {
      if (contentPack.value.active === undefined) {
        contentPack.value.active = true
      }
    })

    onMounted(loadAcademies)
    watch(() => contentPack.value.id, loadAcademies)

    const onCancel = () => {
      emit('cancel')
    }

    const onUpdateContentPack = updatedItem => {
      contentPack.value = updatedItem
    }

    const onSave = async () => {
      await nextTick()
      const isValid = await formObserver.value.validate()

      if (isValid) {
        emit('save', { ...contentPack.value })
      }
    }

    const removeAcademy = async academyId => {
      await deleteContentPackAcademy(academyId, contentPack.value.id)
      assignedAcademies.value = assignedAcademies.value.filter(item => item.id !== academyId)
      emit('relations-updated')
    }

    return {
      formObserver,
      contentPack,
      assignedAcademies,
      formTitle,
      onCancel,
      onUpdateContentPack,
      onSave,
      removeAcademy,
      formatPurchased,
      icons: {
        mdiDeleteOutline,
      },
    }
  },
}
</script>

<style scoped>
.academy-assignment-panel {
  border-color: #d9d9e8 !important;
}

.academy-assignment-header {
  min-height: 28px;
}

.academy-assignment-purchased {
  min-width: 72px;
  text-align: center;
}
</style>
